import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const API_BASE = 'https://system.sales-management.cloud/api/educational_materials.php';

const MATERIAL_TYPES = {
    text: { label: 'テキスト', icon: '📄' },
    image: { label: '画像', icon: '🖼️' },
    video: { label: '動画', icon: '🎥' },
    pdf: { label: 'PDF', icon: '📑' }
};

function MaterialManagement() {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');
    const pageSize = 20;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        material_type: 'text',
        file_path: '',
        file_size: 0,
        is_published: false
    });

    const [uploadFile, setUploadFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    const normalizeMaterial = (material) => ({
        ...material,
        file_size: Number(material.file_size || 0),
        is_published: Number(material.is_published) === 1
    });

    useEffect(() => {
        fetchMaterials(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMaterials = async (page = 1) => {
        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('auth_token');

            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const params = new URLSearchParams({
                action: 'list',
                limit: pageSize,
                offset: (page - 1) * pageSize
            });

            if (search) params.append('search', search);
            if (filterType) params.append('category', filterType);

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(data?.message || `API Error: ${response.status}`);
            }

            if (data?.status === 'success') {
                const normalizedMaterials = (data.data.materials || []).map(normalizeMaterial);
                setMaterials(normalizedMaterials);
                setTotalCount(data.data.total);
                setCurrentPage(page);
            } else {
                setError(data?.message || '教材の取得に失敗しました');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setError(`エラー: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadFile(file);
            setUploadProgress(0);
        }
    };

    const uploadMaterialFile = async () => {
        if (!uploadFile) return null;

        try {
            const token = Cookies.get('auth_token');
            const formData = new FormData();
            formData.append('file', uploadFile);

            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const percentComplete = (e.loaded / e.total) * 100;
                    setUploadProgress(percentComplete);
                }
            });

            return new Promise((resolve, reject) => {
                xhr.addEventListener('load', () => {
                    const isSuccessStatus = xhr.status >= 200 && xhr.status < 300;

                    if (!isSuccessStatus) {
                        reject(new Error(`Upload failed: ${xhr.status}`));
                        return;
                    }

                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (response.status === 'success') {
                            resolve(response.data);
                        } else {
                            reject(new Error(response.message || 'Upload failed'));
                        }
                    } catch (parseError) {
                        reject(new Error('Upload response parse error'));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Upload error'));
                });

                xhr.open('POST', `${API_BASE}?action=upload`);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.send(formData);
            });
        } catch (error) {
            throw new Error(`File upload error: ${error.message}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('auth_token');

            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            let file_data = formData;

            // ファイルがある場合はアップロード
            if (uploadFile) {
                const uploadedFile = await uploadMaterialFile();
                file_data = {
                    ...formData,
                    file_path: uploadedFile.file_path,
                    file_size: uploadedFile.file_size
                };
            }

            const url = isEditing
                ? `${API_BASE}?action=update&id=${selectedMaterial.id}`
                : `${API_BASE}?action=create`;

            const method = isEditing ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(file_data)
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuccess(isEditing ? '教材を更新しました' : '教材を作成しました');
                setShowModal(false);
                resetForm();
                fetchMaterials(currentPage);
                setUploadProgress(0);
            } else {
                setError(data.message || '保存に失敗しました');
            }
        } catch (error) {
            console.error('Submit error:', error);
            setError(`エラー: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (material) => {
        setSelectedMaterial(material);
        setFormData({
            title: material.title,
            description: material.description,
            material_type: material.material_type,
            file_path: material.file_path,
            file_size: material.file_size,
            is_published: material.is_published
        });
        setIsEditing(true);
        setShowModal(true);
        setUploadFile(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('この教材を削除してもよろしいですか？')) {
            return;
        }

        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('auth_token');

            const response = await fetch(`${API_BASE}?action=delete&id=${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuccess('教材を削除しました');
                fetchMaterials(currentPage);
            } else {
                setError(data.message || '削除に失敗しました');
            }
        } catch (error) {
            console.error('Delete error:', error);
            setError(`エラー: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const togglePublish = async (material) => {
        try {
            const token = Cookies.get('auth_token');

            const response = await fetch(`${API_BASE}?action=update&id=${material.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    is_published: !material.is_published
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuccess(material.is_published ? '非公開にしました' : '公開しました');
                fetchMaterials(currentPage);
            } else {
                setError(data.message || '更新に失敗しました');
            }
        } catch (error) {
            setError(`エラー: ${error.message}`);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            material_type: 'text',
            file_path: '',
            file_size: 0,
            is_published: false
        });
        setSelectedMaterial(null);
        setIsEditing(false);
        setUploadFile(null);
        setUploadProgress(0);
    };

    const handleSearch = () => {
        fetchMaterials(1);
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* ヘッダー */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">教材管理</h1>
                    <p className="text-gray-600">テキスト、画像、動画、PDFなどの教材を管理します</p>
                </div>

                {/* エラー/成功メッセージ */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex justify-between items-center">
                        <span>{error}</span>
                        <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
                    </div>
                )}
                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex justify-between items-center">
                        <span>{success}</span>
                        <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">✕</button>
                    </div>
                )}

                {/* 検索・フィルター */}
                <div className="mb-6 bg-white rounded-lg shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">キーワード検索</label>
                            <div className="flex">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="タイトル、説明で検索"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600"
                                >
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">教材タイプ</label>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">すべて</option>
                                {Object.entries(MATERIAL_TYPES).map(([key, config]) => (
                                    <option key={key} value={key}>{config.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <button
                                onClick={() => fetchMaterials(currentPage)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex justify-center items-center gap-2"
                            >
                                <RefreshIcon sx={{ fontSize: 20 }} />
                                更新
                            </button>
                        </div>

                        <div>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                                className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex justify-center items-center gap-2"
                            >
                                <AddIcon sx={{ fontSize: 20 }} />
                                新規作成
                            </button>
                        </div>
                    </div>
                </div>

                {/* テーブル */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">
                            読み込み中...
                        </div>
                    ) : materials.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            教材がありません
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">タイトル</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">タイプ</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">ファイルサイズ</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">作成日</th>
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">ステータス</th>
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {materials.map(material => (
                                        <tr key={material.id} className="border-b hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">{MATERIAL_TYPES[material.material_type]?.icon || '📄'}</span>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{material.title}</p>
                                                        <p className="text-xs text-gray-500">{material.description?.substring(0, 50)}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                                    {MATERIAL_TYPES[material.material_type]?.label || material.material_type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {(material.file_size / (1024 * 1024)).toFixed(2)} MB
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {new Date(material.created_at).toLocaleDateString('ja-JP')}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => togglePublish(material)}
                                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                                        material.is_published
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    } hover:opacity-80`}
                                                >
                                                    {material.is_published ? (
                                                        <>
                                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                                            公開
                                                        </>
                                                    ) : (
                                                        <>
                                                            <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                                            非公開
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(material)}
                                                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                        title="編集"
                                                    >
                                                        <EditIcon sx={{ fontSize: 20 }} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(material.id)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                        title="削除"
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 20 }} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ページング */}
                {totalPages > 1 && (
                    <div className="mt-6 flex justify-center items-center gap-2">
                        <button
                            onClick={() => fetchMaterials(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
                        >
                            前へ
                        </button>
                        <span className="text-gray-600">
                            ページ {currentPage} / {totalPages}
                        </span>
                        <button
                            onClick={() => fetchMaterials(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
                        >
                            次へ
                        </button>
                    </div>
                )}

                {/* モーダル */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {isEditing ? '教材を編集' : '新規教材を作成'}
                                </h2>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        タイトル *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleFormChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        説明
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        教材タイプ *
                                    </label>
                                    <select
                                        name="material_type"
                                        value={formData.material_type}
                                        onChange={handleFormChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {Object.entries(MATERIAL_TYPES).map(([key, config]) => (
                                            <option key={key} value={key}>
                                                {config.icon} {config.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ファイル
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <label className="flex-1 flex items-center justify-center px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50">
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                className="hidden"
                                            />
                                            <div className="text-center">
                                                <CloudUploadIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
                                                <p className="text-sm text-gray-600">
                                                    ファイルをドラッグするか、クリックで選択
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    最大100MB
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                    {uploadFile && (
                                        <div className="mt-2 p-2 bg-blue-50 rounded">
                                            <p className="text-sm text-gray-700">
                                                選択ファイル: {uploadFile.name}
                                            </p>
                                        </div>
                                    )}
                                    {uploadProgress > 0 && uploadProgress < 100 && (
                                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-blue-500 h-2 rounded-full"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="is_published"
                                        checked={formData.is_published}
                                        onChange={handleFormChange}
                                        className="rounded w-4 h-4"
                                    />
                                    <label className="ml-2 text-sm text-gray-700">
                                        公開する
                                    </label>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        キャンセル
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                                    >
                                        {loading ? '保存中...' : '保存'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MaterialManagement;
